import React, { useState, ChangeEvent } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Link,
  Radio,
  RadioGroup,
  Text,
  MultiSearchSelector,
  Option,
  useTranslation,
  GridItem,
} from "@familyzone/component-library";
import { Grid } from "@chakra-ui/react";
import { SectionHeading } from "../../shared/SectionHeading";
import { optionArrayToStringArray, stringArrayToOptionArray } from "../../shared/DomainsMultiSelectInput/utils";
import { TabTypes } from ".";

interface VideoSettingsTabProps {
  saving: boolean;
  disabled: boolean;
  onSave: (tabType: TabTypes) => void;
  onReset: () => void;
  resetSettingsDisabled: boolean;

  videoGoreDetection: boolean;
  setVideoGoreDetectionEnabled: (val: boolean) => void;
  videoGoreSensitivity: number;
  setVideoGoreSensitivity: (val: number) => void;

  videoPornDetection: boolean;
  setVideoPornDetectionEnabled: (val: boolean) => void;
  videoPornSensitivity: number;
  setVideoPornSensitivity: (val: number) => void;

  videoIntimateApparelDetection: boolean;
  setVideoIntimateApparelDetectionEnabled: (val: boolean) => void;
  videoIntimateApparelSensitivity: number;
  setVideoIntimateApparelSensitivity: (val: number) => void;

  videoBlockVideo: boolean;
  setVideoBlockVideo: (val: boolean) => void;

  videoYoutubeChannelExceptions: string[];
  setVideoYoutubeChannelExceptions: (val: string[]) => void;
}

const VideoSettingsTab = ({
  saving,
  disabled,
  onSave,
  onReset,
  resetSettingsDisabled,

  videoGoreDetection,
  setVideoGoreDetectionEnabled,
  videoGoreSensitivity,
  setVideoGoreSensitivity,

  videoPornDetection,
  setVideoPornDetectionEnabled,
  videoPornSensitivity,
  setVideoPornSensitivity,

  videoIntimateApparelDetection,
  setVideoIntimateApparelDetectionEnabled,
  videoIntimateApparelSensitivity,
  setVideoIntimateApparelSensitivity,

  videoBlockVideo,
  setVideoBlockVideo,

  videoYoutubeChannelExceptions,
  setVideoYoutubeChannelExceptions,
}: VideoSettingsTabProps): JSX.Element => {
  const { t } = useTranslation();
  const [youtubeChannelOptions, setYoutubeChannelOptions] = useState<string[]>([]);

  function inputChanged(e: ChangeEvent<HTMLInputElement>) {
    let inputChannel = e.target.value.replace(/\s/g, "").toLowerCase();
    inputChannel = inputChannel.startsWith("@") ? inputChannel : `@${inputChannel}`;
    setYoutubeChannelOptions([inputChannel]);
  }

  function selectedValuesChanged(selectedOptions: Option[]) {
    setVideoYoutubeChannelExceptions(optionArrayToStringArray(selectedOptions));
    setYoutubeChannelOptions([]);
  }

  return (
    <Flex direction="column" minH="100%">
      <Box backgroundColor="white" borderRadius="2xl" boxShadow="sm" pt="sp24">
        <Box px="sp24">
          <SectionHeading title={t("Categories")} aria-level={2} />
          <Grid mt="sp36" templateColumns="repeat(2, 1fr)" columnGap="sp4">
            <GridItem colSpan={1} mt="sp24" ml="sp12" mr="sp12">
              <Text color="neutrals.120">{t("Set the content categories and detection sensitivity for Content Aware.")}</Text>
              <Grid templateColumns="20px 1fr" columnGap="sp8" alignItems="start" mt="sp16">
                <Checkbox
                  isChecked={videoGoreDetection}
                  isDisabled={disabled}
                  onChange={(e) => setVideoGoreDetectionEnabled(e.target.checked)}
                  aria-label={`Gore detection ${videoGoreDetection ? "enabled" : "disabled"}`}
                />
                <Box>
                  <Text fontWeight="medium" mt="-sp2">
                    {t("Gore detection")}
                  </Text>
                  {videoGoreDetection && (
                    <Flex mt="sp8" alignItems="center" flexWrap="wrap">
                      <Text mr="sp8" color="neutrals.600">
                        {t("Sensitivity level:")}
                      </Text>
                      <RadioGroup direction="row" value={videoGoreSensitivity} onChange={(v: string) => setVideoGoreSensitivity(Number(v))}>
                        <Radio value={0} isDisabled={disabled}>
                          {t("Low")}
                        </Radio>
                        <Radio value={0.5} isDisabled={disabled}>
                          {t("Medium")}
                        </Radio>
                        <Radio value={1} isDisabled={disabled}>
                          {t("High")}
                        </Radio>
                      </RadioGroup>
                    </Flex>
                  )}
                </Box>
              </Grid>

              <Grid templateColumns="20px 1fr" columnGap="sp8" alignItems="start" mt="sp16">
                <Checkbox
                  isChecked={videoPornDetection}
                  isDisabled={disabled}
                  onChange={(e) => setVideoPornDetectionEnabled(e.target.checked)}
                  aria-label={`Pornography detection ${videoPornDetection ? "enabled" : "disabled"}`}
                />
                <Box>
                  <Text fontWeight="medium" mt="-sp2">
                    {t("Pornography detection")}
                  </Text>
                  {videoPornDetection && (
                    <Flex mt="sp8" alignItems="center" flexWrap="wrap">
                      <Text mr="sp8" color="neutrals.600">
                        {t("Sensitivity level:")}
                      </Text>
                      <RadioGroup direction="row" value={videoPornSensitivity} onChange={(v: string) => setVideoPornSensitivity(Number(v))}>
                        <Radio value={0} isDisabled={disabled}>
                          {t("Low")}
                        </Radio>
                        <Radio value={0.5} isDisabled={disabled}>
                          {t("Medium")}
                        </Radio>
                        <Radio value={1} isDisabled={disabled}>
                          {t("High")}
                        </Radio>
                      </RadioGroup>
                    </Flex>
                  )}
                </Box>
              </Grid>

              <Grid templateColumns="20px 1fr" columnGap="sp8" alignItems="start" mt="sp16">
                <Checkbox
                  isChecked={videoIntimateApparelDetection}
                  isDisabled={disabled}
                  onChange={(e) => setVideoIntimateApparelDetectionEnabled(e.target.checked)}
                  aria-label={`Intimate Apparel detection ${videoIntimateApparelDetection ? "enabled" : "disabled"}`}
                />
                <Box>
                  <Text fontWeight="medium" mt="-sp2">
                    {t("Intimate Apparel detection")}
                  </Text>
                  {videoIntimateApparelDetection && (
                    <Flex mt="sp8" alignItems="center" flexWrap="wrap">
                      <Text mr="sp8" color="neutrals.600">
                        {t("Sensitivity level:")}
                      </Text>
                      <RadioGroup
                        direction="row"
                        value={videoIntimateApparelSensitivity}
                        onChange={(v: string) => setVideoIntimateApparelSensitivity(Number(v))}
                      >
                        <Radio value={0} isDisabled={disabled}>
                          {t("Low")}
                        </Radio>
                        <Radio value={0.5} isDisabled={disabled}>
                          {t("Medium")}
                        </Radio>
                        <Radio value={1} isDisabled={disabled}>
                          {t("High")}
                        </Radio>
                      </RadioGroup>
                    </Flex>
                  )}
                </Box>
              </Grid>
            </GridItem>

            <GridItem colSpan={1} mt="sp24" mr="sp12">
              <Box backgroundColor="#E5F9F6" borderRadius="md" px="sp16" py="sp8" maxW="520px">
                <Flex alignItems="center" mb="sp12" gap="sp12">
                  <Icon icon="fa-info-circle" color="teal" />
                  <Text fontWeight="medium">{t("Sensitivity levels explained")}</Text>
                </Flex>

                <Grid templateColumns="80px 1fr" rowGap="sp8" columnGap="sp12">
                  <Text pl="sp24" color="neutrals.700" fontSize="13.5px">
                    {t("Low")}
                  </Text>
                  <Text color="neutrals.700" fontSize="13.5px">
                    {t("Blurs content when detection confidence is high, less false positives.")}
                  </Text>

                  <Text pl="sp24" color="neutrals.700" fontSize="13.5px">
                    {t("Medium")}
                  </Text>
                  <Text color="neutrals.700" fontSize="13.5px">
                    {t("Blurs content at a moderate detection confidence, moderate chance of false positives.")}
                  </Text>

                  <Text pl="sp24" color="neutrals.700" fontSize="13.5px">
                    {t("High")}
                  </Text>
                  <Text color="neutrals.700" fontSize="13.5px">
                    {t("Blurs content with low detection confidence, higher chance of false positives.")}
                  </Text>
                </Grid>

                <Text mt="sp8" fontStyle="italic" color="neutrals.700" pl="sp24" fontSize="13.5px">
                  {t("For more information about Content Aware settings please")}{" "}
                  <Link
                    textDecoration="underline"
                    href="https://help.linewize.com/hc/en-gb/articles/21788694224412"
                    isExternal={true}
                    style={{ color: "#156cb0" }}
                  >
                    read our knowledge article.
                  </Link>
                </Text>
              </Box>
            </GridItem>
          </Grid>

          <Box mt="sp24">
            <SectionHeading title={t("Block video")} aria-level={2} />
            <Text color="neutrals.120" mt="sp8" ml="sp12">
              {t("Automatically blocks both the audio and visual components of a video if it matches a configured content category.")}
            </Text>
            <Box mt="sp16" ml="sp12" color="neutrals.120">
              <Checkbox
                isChecked={videoBlockVideo}
                isDisabled={disabled}
                aria-label={`Turn on Block Video ${videoBlockVideo ? "enabled" : "disabled"}`}
                onChange={(e) => setVideoBlockVideo(e.target.checked)}
              >
                <Text fontWeight="medium">{t("Turn on Block Video")}</Text>
              </Checkbox>
            </Box>
          </Box>

          <Box mt="sp16">
            <SectionHeading title={t("Excluded Youtube channels")} aria-level={2} />
            <Text color="neutrals.120" mt="sp8" ml="sp12">
              {t("Content Aware won't apply on videos from these Youtube channels.")}
            </Text>

            <Box mt="sp16" ml="sp12">
              <Text color="neutrals.120" fontWeight="medium">
                {t("Youtube channels")}
              </Text>
              <Box mt="sp8">
                <MultiSearchSelector
                  data-testid="youtube-whitelist-multi-select-input"
                  placeholder={"Paste the channel name contained with the youtube URL (for example, @ChannelName)"}
                  options={stringArrayToOptionArray(youtubeChannelOptions)}
                  selected={stringArrayToOptionArray(videoYoutubeChannelExceptions)}
                  onInputChange={inputChanged}
                  onChange={selectedValuesChanged}
                  clearInputOnSelect
                  filterOptions={false}
                  disabled={disabled}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box bg="#F5FCFF" mt="sp24" px="sp24" py="sp24" borderBottomLeftRadius="2xl" borderBottomRightRadius="2xl">
          <Flex>
            <Button
              variant="primary"
              disabled={disabled}
              onClick={() => onSave(TabTypes.VIDEO)}
              data-testid="save-video-content-aware-config-button"
              aria-label={saving ? t("Saving...") : t("Save Changes")}
            >
              {saving ? (
                <div style={{ width: "90px", margin: "1px 0" }}>{t("Saving...")}</div>
              ) : (
                <div style={{ width: "90px", margin: "1px 0" }}>{t("Save Changes")}</div>
              )}
            </Button>
            <Button
              ml="sp12"
              variant={resetSettingsDisabled ? undefined : "outline"}
              disabled={disabled || resetSettingsDisabled}
              onClick={onReset}
              data-testid="reset-video-content-aware-config-button"
            >
              {t("Reset Video Settings")}
            </Button>
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
};

export default VideoSettingsTab;
